/**
 * @file api-shop-carriers.ts
 * @description API-клиент для модуля «Доставка» в shop-кабинете
 * (phase10.5.Y Carrier Management Module).
 *
 * Эндпоинты бэкенда:
 *  - GET    /admin/shop/carriers
 *  - PUT    /admin/shop/carriers/:carrier
 *  - DELETE /admin/shop/carriers/:carrier
 *  - POST   /admin/shop/carriers/:carrier/sync-pickup-points
 *  - GET    /admin/shop/carriers/:carrier/pickup-points
 *  - GET    /admin/shop/carriers/:carrier/sync-log    — журнал синка (phase19.12)
 *  - GET    /admin/shop/carriers/:carrier/city-search — справочник НП (phase19.12)
 *  - POST   /admin/shop/carriers/:carrier/quote      — расчёт (phase19.12)
 *  - POST   /admin/shop/carriers/:carrier/diagnose   — проверка (phase19.12)
 *  - POST   /admin/shop/carriers/:carrier/webhook/register — подписка на
 *    статусы доставки (phase19.13 шаг 7)
 *  - DELETE /admin/shop/carriers/:carrier/webhook    — снять подписку
 */

import { apiFetch } from "./api-client";

export type CarrierKind = "SDEK";

/** Как посылка попадает к перевозчику (phase19.12). */
export type CarrierHandoverMode = "DROP_AT_PICKUP_POINT" | "COURIER_INTAKE";

/** Известные коды тарифов СДЭК для чекбоксов «тарифы на витрине». */
export const SDEK_TARIFF_OPTIONS: Array<{ code: number; label: string }> = [
  { code: 136, label: "Посылка склад-склад (до ПВЗ)" },
  { code: 137, label: "Посылка склад-дверь (курьером)" },
  { code: 138, label: "Посылка дверь-склад" },
  { code: 139, label: "Посылка дверь-дверь" },
];

/** Страны, по которым СДЭК отдаёт ПВЗ — реестр для чекбоксов области синка. */
export const SDEK_SYNC_COUNTRIES: Array<{ code: string; label: string }> = [
  { code: "RU", label: "Россия" },
  { code: "KZ", label: "Казахстан" },
  { code: "BY", label: "Беларусь" },
  { code: "AM", label: "Армения" },
  { code: "KG", label: "Киргизия" },
  { code: "UZ", label: "Узбекистан" },
];

/**
 * Чем рисуем карту ПВЗ на витрине (решение пользователя 2026-08-14):
 * AUTO — Яндекс с автоматическим откатом на OSM при нерабочем ключе.
 */
export type CheckoutMapProvider = "AUTO" | "YANDEX" | "OSM";

/** Режим области синхронизации ПВЗ. */
export type PickupSyncScopeMode = "ALL" | "COUNTRIES" | "CITIES";

/** Населённый пункт из справочника перевозчика (код + отображаемое имя). */
export interface PickupSyncCity {
  code: number;
  name: string;
}

/** Что именно тянем из API перевозчика при синхронизации кэша ПВЗ. */
export interface PickupSyncScope {
  mode: PickupSyncScopeMode;
  countries?: string[];
  cities?: PickupSyncCity[];
}

/**
 * Область по умолчанию: сервер трактует пустой scope как «только Россия»,
 * поэтому UI показывает то же самое (один источник правды по поведению).
 */
export const DEFAULT_PICKUP_SYNC_SCOPE: PickupSyncScope = {
  mode: "COUNTRIES",
  countries: ["RU"],
};

/** null/мусор из API → рабочая область по умолчанию. */
export function normalizePickupSyncScope(
  scope: PickupSyncScope | null | undefined,
): PickupSyncScope {
  if (!scope) return { ...DEFAULT_PICKUP_SYNC_SCOPE, countries: ["RU"] };
  return {
    mode: scope.mode,
    countries: scope.countries ?? [],
    cities: scope.cities ?? [],
  };
}

/** Агрегат по shipment'ам за 30 дней для отображения «здоровья» ТК. */
export interface CarrierStatsDto {
  totalLast30d: number;
  delivered: number;
  inTransit: number;
  returned: number;
  /** 0-100. */
  deliveryRate: number;
}

/** Что отдаёт бэкенд про конфиг ТК конкретного магазина. */
export interface ShopCarrierConfigDto {
  id: string | null;
  shopId: string;
  carrier: CarrierKind;
  carrierLabel: string;
  /** Подключена ли credentials-интеграция на платформе. */
  integrationConfigured: boolean;
  integrationId: string | null;
  /** Сам ShopCarrierConfig существует? false = ТК ещё не подключена к магазину. */
  configured: boolean;
  isActive: boolean;
  priority: number;
  cashOnDelivery: boolean;
  onlinePayment: boolean;
  cityWhitelist: string[];
  maxWeightG: number | null;
  maxDimensions: string | null;
  // ── phase19.12 — параметры расчёта доставки ──────────────────────────────
  senderCityCode: number | null;
  senderPostalCode: string | null;
  senderAddress: string | null;
  senderPickupPointCode: string | null;
  handoverMode: CarrierHandoverMode;
  defaultBoxLengthMm: number | null;
  defaultBoxWidthMm: number | null;
  defaultBoxHeightMm: number | null;
  fallbackWeightG: number | null;
  publicTariffCodes: number[];
  markupPercent: number;
  markupAmount: number;
  freeShippingThreshold: number | null;
  webhookSubscriptionUuid: string | null;
  yandexMapsApiKey: string | null;
  /** Провайдер карты ПВЗ на витрине (дефолт AUTO). */
  checkoutMapProvider: CheckoutMapProvider;
  lastPickupSync: string | null;
  /** Активных ПВЗ в локальном кэше — вместе с датой синка видно свежесть. */
  pickupPointCount: number;
  /** Область синхронизации кэша ПВЗ (null = дефолт сервера «только Россия»). */
  pickupSyncScope: PickupSyncScope | null;
  /** Период автообновления кэша ПВЗ в часах (null = автообновление выключено). */
  pickupSyncIntervalHours: number | null;
  /**
   * Период автоопроса статусов доставки в минутах (phase19.13 шаг 4).
   * 0 — автоопрос выключен, статусы приходят только по вебхуку.
   */
  deliverySyncIntervalMinutes: number;
  stats: CarrierStatsDto;
  createdAt: string | null;
  updatedAt: string | null;
}

/** Тело PUT /admin/shop/carriers/:carrier. */
export interface UpsertShopCarrierConfigInput {
  isActive?: boolean;
  priority?: number;
  cashOnDelivery?: boolean;
  onlinePayment?: boolean;
  cityWhitelist?: string[];
  maxWeightG?: number | null;
  maxDimensions?: string | null;
  integrationId?: string | null;
  // phase19.12
  senderCityCode?: number | null;
  senderPostalCode?: string | null;
  senderAddress?: string | null;
  senderPickupPointCode?: string | null;
  handoverMode?: CarrierHandoverMode;
  defaultBoxLengthMm?: number | null;
  defaultBoxWidthMm?: number | null;
  defaultBoxHeightMm?: number | null;
  fallbackWeightG?: number | null;
  publicTariffCodes?: number[];
  markupPercent?: number;
  markupAmount?: number;
  freeShippingThreshold?: number | null;
  yandexMapsApiKey?: string | null;
  checkoutMapProvider?: CheckoutMapProvider;
  pickupSyncScope?: PickupSyncScope | null;
  pickupSyncIntervalHours?: number | null;
  // phase19.13 — автоопрос статусов доставки (0-1440, 0 = выключен)
  deliverySyncIntervalMinutes?: number;
}

// ── phase19.12 — расчёт и диагностика ──────────────────────────────────────

export interface DeliveryQuoteOption {
  tariffCode: number;
  tariffName: string;
  description: string | null;
  /** Цена для клиента: тариф + наценка магазина, 0 при бесплатной доставке. */
  price: number;
  /** Цена перевозчика до наценки. */
  carrierPrice: number;
  free: boolean;
  periodMin: number | null;
  periodMax: number | null;
}

/** Габариты одного места (коробки) в запросе/ответе расчёта. */
export interface DeliveryQuotePackage {
  weightG: number;
  lengthMm?: number;
  widthMm?: number;
  heightMm?: number;
}

export interface DeliveryQuoteResult {
  available: boolean;
  reason?: string;
  options: DeliveryQuoteOption[];
  packageUsed: DeliveryQuotePackage;
  warnings: string[];
  /** Интеграция без ключей — цены фиктивные, клиенту называть нельзя. */
  mock: boolean;
  /** Тестовый контур перевозчика (edu-среда СДЭК). */
  sandbox: boolean;
}

export interface AdminDeliveryQuoteInput {
  items?: Array<{ skuId: string; quantity: number }>;
  /** @deprecated phase19.12 шаг 5 — одиночное место; используйте `packages`. */
  package?: DeliveryQuotePackage;
  /** Несколько мест (коробок) — приоритетнее одиночного `package`. */
  packages?: DeliveryQuotePackage[];
  pickupPointCode?: string;
  cityCode?: number;
  postalCode?: string;
  city?: string;
  address?: string;
  goodsTotal?: number;
}

/** Расчёт доставки для менеджера: все тарифы + ручное место. */
export async function quoteCarrierDelivery(
  carrier: CarrierKind,
  input: AdminDeliveryQuoteInput,
): Promise<DeliveryQuoteResult> {
  return apiFetch<DeliveryQuoteResult>(`/admin/shop/carriers/${carrier}/quote`, {
    method: "POST",
    json: input,
  });
}

export type DiagnosticStatus = "ok" | "warn" | "fail";

export interface DiagnosticCheck {
  key: string;
  title: string;
  status: DiagnosticStatus;
  message: string;
}

export interface CarrierDiagnosticsResult {
  ok: boolean;
  checks: DiagnosticCheck[];
}

/** «Проверить настройки» — живые вызовы, только по кнопке. */
export async function diagnoseCarrier(
  carrier: CarrierKind,
): Promise<CarrierDiagnosticsResult> {
  return apiFetch<CarrierDiagnosticsResult>(`/admin/shop/carriers/${carrier}/diagnose`, {
    method: "POST",
  });
}

// ── phase19.13 шаг 7 — подписка на статусы доставки ─────────────────────────

/**
 * Итог управления подпиской на вебхук перевозчика.
 *
 * URL приёмника задаётся окружением, а не руками в форме: опечатка означала бы
 * «подписка есть, событий нет». `message` — готовый русский текст сервера, его
 * и показываем в тосте.
 */
export interface CarrierWebhookResult {
  /** Идентификатор подписки в ИС перевозчика; null — подписки больше нет. */
  uuid: string | null;
  /** Адрес приёмника, на который оформлена подписка. */
  url: string | null;
  /** Подписка уже была на этот же адрес — ничего не создавали. */
  alreadyExists: boolean;
  /** Нашли подписку на другой адрес и пересоздали её. */
  replaced: boolean;
  /** Интеграции нет — работали на заглушках, у перевозчика ничего не менялось. */
  mock: boolean;
  message: string;
}

/**
 * Коды ошибок подписки → что делать менеджеру. Реестр: новый код сервера =
 * запись здесь, а не ветка в компоненте.
 */
export const CARRIER_WEBHOOK_ERROR_TEXT: Record<string, string> = {
  sdek_webhook_url_not_configured:
    "URL приёмника не настроен в окружении — попросите платформенного администратора " +
    "задать адрес вебхука и повторите.",
  carrier_config_not_found:
    "Перевозчик ещё не подключён к магазину — сохраните настройки на вкладке «Основное».",
  carrier_webhook_unsupported: "Этот перевозчик не поддерживает подписку на статусы.",
};

/** Оформить подписку на статусы доставки. */
export async function registerCarrierWebhook(
  carrier: CarrierKind,
): Promise<CarrierWebhookResult> {
  return apiFetch<CarrierWebhookResult>(
    `/admin/shop/carriers/${carrier}/webhook/register`,
    { method: "POST" },
  );
}

/** Снять подписку на статусы доставки. */
export async function unregisterCarrierWebhook(
  carrier: CarrierKind,
): Promise<CarrierWebhookResult> {
  return apiFetch<CarrierWebhookResult>(`/admin/shop/carriers/${carrier}/webhook`, {
    method: "DELETE",
  });
}

/** Получить список всех известных ТК для shop'а. */
export async function listShopCarriers(): Promise<ShopCarrierConfigDto[]> {
  return apiFetch<ShopCarrierConfigDto[]>("/admin/shop/carriers");
}

/** Создать/обновить конфигурацию ТК для shop'а. */
export async function upsertShopCarrier(
  carrier: CarrierKind,
  input: UpsertShopCarrierConfigInput,
): Promise<ShopCarrierConfigDto> {
  return apiFetch<ShopCarrierConfigDto>(`/admin/shop/carriers/${carrier}`, {
    method: "PUT",
    json: input,
  });
}

/** Удалить (отключить полностью) ТК у shop'а. */
export async function removeShopCarrier(carrier: CarrierKind): Promise<void> {
  await apiFetch<void>(`/admin/shop/carriers/${carrier}`, { method: "DELETE" });
}

/**
 * Запустить ФОНОВЫЙ sync ПВЗ из API перевозчика → локальный cache.
 * Отвечает мгновенно: прогресс смотрим в журнале (`fetchCarrierSyncLog`).
 */
export async function syncCarrierPickupPoints(
  carrier: CarrierKind,
): Promise<{ logId: string; alreadyRunning: boolean; mock: boolean }> {
  return apiFetch(`/admin/shop/carriers/${carrier}/sync-pickup-points`, {
    method: "POST",
  });
}

/** Чем запущена синхронизация: кнопкой в админке или планировщиком. */
export type PickupSyncTrigger = "MANUAL" | "AUTO";

/** Состояние записи журнала синхронизации. */
export type PickupSyncStatus = "RUNNING" | "SUCCESS" | "ERROR";

/** Строка журнала синхронизации кэша ПВЗ. */
export interface CarrierSyncLogEntry {
  id: string;
  trigger: PickupSyncTrigger;
  status: PickupSyncStatus;
  scope: unknown;
  added: number;
  updated: number;
  deactivated: number;
  totalAfter: number;
  durationMs: number | null;
  errorMessage: string | null;
  startedAt: string;
  finishedAt: string | null;
}

/** История последних синхронизаций (по умолчанию 10 записей). */
export async function fetchCarrierSyncLog(
  carrier: CarrierKind,
  limit = 10,
): Promise<CarrierSyncLogEntry[]> {
  return apiFetch<CarrierSyncLogEntry[]>(
    `/admin/shop/carriers/${carrier}/sync-log?limit=${limit}`,
  );
}

/** Подсказка населённого пункта из справочника перевозчика. */
export interface CarrierCitySuggestion {
  code: number;
  city: string;
  region?: string;
}

/** Поиск НП по справочнику перевозчика (query от 2 символов). */
export async function searchCarrierCities(
  carrier: CarrierKind,
  query: string,
): Promise<CarrierCitySuggestion[]> {
  const search = new URLSearchParams({ query });
  return apiFetch<CarrierCitySuggestion[]>(
    `/admin/shop/carriers/${carrier}/city-search?${search.toString()}`,
  );
}

/** ПВЗ через универсальный shop-эндпоинт (proxy на SdekService). */
export interface ShopPickupPointDto {
  id: string;
  carrier: CarrierKind;
  externalCode: string;
  city: string;
  region: string | null;
  address: string;
  lat: number | null;
  lng: number | null;
  workingHours: string | null;
  /** Контакты пункта из справочника перевозчика (могут отсутствовать). */
  phone: string | null;
  email: string | null;
  hasCashPay: boolean;
  isActive: boolean;
}

export async function listCarrierPickupPoints(
  carrier: CarrierKind,
  params: { city?: string; region?: string } = {},
): Promise<ShopPickupPointDto[]> {
  const search = new URLSearchParams();
  if (params.city) search.set("city", params.city);
  if (params.region) search.set("region", params.region);
  const qs = search.toString();
  return apiFetch<ShopPickupPointDto[]>(
    `/admin/shop/carriers/${carrier}/pickup-points${qs ? `?${qs}` : ""}`,
  );
}
